/**
 * @file GraficoLeadsPorEtapa.jsx
 * @description Componente que exibe um gráfico de barras mostrando a distribuição
 * de leads por etapa do funil de vendas, junto com métricas de desempenho.
 */

import { Box, CircularProgress, IconButton, Tooltip, Typography } from "@mui/material";
import HelpOutlineTwoToneIcon from "@mui/icons-material/HelpOutlineTwoTone";
import GraficoBarrasPadrao from "../Charts/GraficoBarraPadrao";

/**
 * Componente GraficoLeadsPorEtapa
 * 
 * Exibe um gráfico de barras com informações sobre leads em diferentes etapas
 * do funil de vendas, além de métricas resumidas como total de leads e taxa de conversão.
 * 
 * @component
 * @param {Object} props - Propriedades do componente
 * @param {Object} props.aggregatedChartData - Dados agregados para o gráfico
 * @param {Array<string>} props.aggregatedChartData.labels - Rótulos para cada barra do gráfico (etapas do funil)
 * @param {Array<number>} props.aggregatedChartData.data - Valores numéricos para cada barra (quantidade de leads)
 * @param {number} props.totalLeads - Número total de leads em todas as etapas
 * @param {number} props.conversionRate - Taxa de conversão em percentual (0-100)
 * @returns {JSX.Element} Componente de gráfico em um container Paper
 */
const GraficoLeadsPorEtapa = ({
  aggregatedChartData,
  totalLeads,
  conversionRate,
  loading = false,
  tooltipText = "",
}) => (
  <Box sx={{
    height: '100%',
    display: 'flex',
    flexDirection: 'column',
    boxShadow: 2,
    borderRadius: 2,
    bgcolor: 'background.paper',
    border: (theme) => `1px solid ${theme.palette.divider}`,
    p: 1.5
  }}>
    {/* Header do Gráfico */}
    <Box sx={{ mb: { xs: 2, md: 3 } }}>
      <Typography
        variant="h6"
        sx={{
          fontWeight: 600,
          color: 'text.primary',
          fontSize: { xs: '1.125rem', md: '1.25rem' },
          mb: 0.5,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          gap: 1,
        }}
      >
        Leads por Etapa
        {tooltipText?.trim() && (
          <Tooltip
            title={tooltipText}
            placement="top"
            componentsProps={{
              tooltip: {
                sx: { fontSize: "0.95rem", lineHeight: 1.4, p: 1.25 },
              },
            }}
          >
            <IconButton
              size="medium"
              aria-label="Ajuda do grafico de leads por etapa"
              sx={{ p: 0.75 }}
            >
              <HelpOutlineTwoToneIcon sx={{ fontSize: 22 }} />
            </IconButton>
          </Tooltip>
        )}
      </Typography>
    </Box>

    {/* Container do Gráfico */}
    <Box
      sx={{
        flex: 1,
        minHeight: { xs: 200, md: 250 },
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'left',
        position: 'relative',
        background: 'linear-gradient(135deg, rgba(102, 126, 234, 0.02) 0%, rgba(118, 75, 162, 0.02) 100%)',
        borderRadius: 2,
        p: { xs: 1, md: 2 },
      }}
      aria-busy={loading ? 'true' : 'false'}
    >
      {/* Componente de gráfico de barras reutilizável */}
      <GraficoBarrasPadrao
        titulo=""  // Título já está sendo exibido acima
        labels={aggregatedChartData.labels}
        data={aggregatedChartData.data}
        legendaY='Quantidade de Leads'
      />
      {loading && (
        <Box
          sx={{
            position: 'absolute',
            inset: 0,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            backgroundColor: (theme) =>
              theme.palette.mode === 'dark' ? 'rgba(11, 16, 32, 0.55)' : 'rgba(255, 255, 255, 0.45)',
            pointerEvents: 'none',
          }}
        >
          <CircularProgress size={32} />
        </Box>
      )}
    </Box>

    {/* Métricas Resumidas */}
    <Box
    >
      <Box
        sx={{
          display: 'flex',
          flexDirection: { xs: 'column', sm: 'row' },
          justifyContent: 'center',
          alignItems: 'center',
          gap: { xs: 1.5, sm: 3 },
          textAlign: 'center'
        }}
      >

        {/* Card de Taxa de Conversão */}
        <Box
          sx={{
            display: 'flex',
            flexDirection: { xs: 'row', sm: 'column' },
            alignItems: 'center',
            justifyContent: { xs: 'space-between', sm: 'center' },
            gap: 0.5,
          }}
        >
          <Typography
            variant="caption"
            sx={{
              fontWeight: 500,
              color: 'text.secondary',
              fontSize: '1rem',
              whiteSpace: 'nowrap'
            }}
          >
            Taxa de Conversão
          </Typography>
          <Typography
            variant="h6"
            sx={{
              fontWeight: 700,
              color: '#4caf50',
              fontSize: '1.1rem',
            }}
          >
            {conversionRate.toFixed(2)}%
          </Typography>
        </Box>
      </Box>
    </Box>
  </Box>
);

export default GraficoLeadsPorEtapa;
